import React from 'react';

import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { getFeedbackStepDetails } from '@/components/shared/FeedbackScale';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import {
	CONGREGATIONAL_STEP_LABEL_MAP,
	PERSONAL_STEP_LABEL_MAP,
	TEAM_STEP_LABEL_MAP,
	UserMilestones,
} from '@/config/constants';
import {
	useCreateSetlistFeedbackSubmissionMutation,
	useListMySetlistFeedbackRequestsQuery,
} from '@/graphql';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { getAcronym } from '@/helpers/textTransform';
import { useAuth } from '@/providers/AuthProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { LimitedTeamSong } from '@/providers/TeamSongsProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
	FeedbackPanelProps,
	TeamSongFeedbackScoresMap,
	TeamSongFeedbackSubmission,
} from './FeedbackPanel';
import styles from './FeedbackPanel.module.scss';

export const TeamSongSubmissionPage: React.FC<{
	setlistId: string;
	setlistTeamSongs: LimitedTeamSong[];
	teamSongFeedbackScoresMap: TeamSongFeedbackScoresMap;
	noToast?: boolean;
	setCurrentTeamSongFeedbackPageIdx: React.Dispatch<
		React.SetStateAction<number>
	>;
	onClose: FeedbackPanelProps['onClose'];
}> = ({
	setlistId,
	setlistTeamSongs,
	teamSongFeedbackScoresMap,
	noToast,
	setCurrentTeamSongFeedbackPageIdx,
	onClose,
}) => {
	const { hasMilestones, appendMilestone } = useAuth();
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	const { mutateAsync, isLoading } =
		useCreateSetlistFeedbackSubmissionMutation();
	const { refetch } = useSetlists();
	const { refetch: refetchFeedback } =
		useListMySetlistFeedbackRequestsQuery();

	return (
		<BottomActionLayout
			contentClassName={styles['submission-page']}
			listClassName={styles['submission-list']}
			actionDivider
			actionContent={
				<>
					<AsaphButton
						color="grey"
						label="Back"
						onClick={() => {
							setCurrentTeamSongFeedbackPageIdx((i) => i - 1);
						}}
						fullWidth
					/>
					<AsaphButton
						color="blue"
						label="Submit"
						fullWidth
						disabled={isLoading}
						onClick={async () => {
							try {
								const res = await mutateAsync({
									input: {
										setlistId,
										teamSongFeedbackSubmissions:
											Object.values(
												teamSongFeedbackScoresMap,
											),
									},
								});
								if (
									!hasMilestones([
										UserMilestones.GIVEN_FIRST_FEEDBACK,
									])
								) {
									appendMilestone(
										UserMilestones.GIVEN_FIRST_FEEDBACK,
									);
								}
								refetchFeedback();
								refetch();
								onClose({ submittedFeedback: true });
								if (noToast) {
									return;
								}
								enqueueSnackbar('Thanks for your feedback!', {
									variant: 'success',
								});
							} catch (err) {
								enqueueSnackbar('Something went wrong', {
									variant: 'error',
								});
								console.error(err);
							}
						}}
					/>
				</>
			}
		>
			<div className={styles['summary-header']}>
				Feedback summary
			</div>
			{setlistTeamSongs.map((teamSong) => (
				<TeamSongSummaryCard
					key={`summary-card-${teamSong.id}`}
					teamSong={teamSong}
					feedbackSubmission={teamSongFeedbackScoresMap[teamSong.id]}
				/>
			))}
			<AveragesCard teamSongFeedbackScoresMap={teamSongFeedbackScoresMap} />
		</BottomActionLayout>
	);
};

const TeamSongSummaryCard: React.FC<{
	teamSong: LimitedTeamSong;
	feedbackSubmission: TeamSongFeedbackSubmission;
}> = ({ teamSong, feedbackSubmission }) => {
	const personalScore = getFeedbackStepDetails(
		feedbackSubmission.personalScore,
		PERSONAL_STEP_LABEL_MAP,
	);
	const teamScore = getFeedbackStepDetails(
		feedbackSubmission.teamScore,
		TEAM_STEP_LABEL_MAP,
	);
	const congregationalScore = getFeedbackStepDetails(
		feedbackSubmission.congregationalScore,
		CONGREGATIONAL_STEP_LABEL_MAP,
	);

	const albumGradient = teamSong.artworkUrl ? null : generateAlbumGradient(teamSong.name);

	return (
		<ModalSectionCard
			className={styles['summary-section']}
			borderRadiusSize="sm"
		>
			<div className={styles['row']}>
				<div
					className={styles['cover-image']}
					style={albumGradient ? albumGradient.style : undefined}
				>
					{teamSong.artworkUrl ? (
						<img src={teamSong.artworkUrl} />
					) : (
						<span style={albumGradient ? { color: albumGradient.textColor } : undefined}>
							{getAcronym(teamSong.name)}
						</span>
					)}
				</div>
				<div className={styles['score']}>
					<div className={styles['emoji']}>{personalScore.emoji}</div>
					<div className={styles['score-label']}>Personal</div>
				</div>
				<div className={styles['score']}>
					<div className={styles['emoji']}>{teamScore.emoji}</div>
					<div className={styles['score-label']}>Team</div>
				</div>
				<div className={styles['score']}>
					<div className={styles['emoji']}>{congregationalScore.emoji}</div>
					<div className={styles['score-label']}>Church</div>
				</div>
			</div>
		</ModalSectionCard>
	);
};

const AveragesCard: React.FC<{
	teamSongFeedbackScoresMap: TeamSongFeedbackScoresMap;
}> = ({ teamSongFeedbackScoresMap }) => {
	const submissions = Object.values(teamSongFeedbackScoresMap);
	const n = submissions.length;
	if (n === 0) return null;

	const avg = (key: keyof TeamSongFeedbackSubmission) =>
		(submissions.reduce((sum, s) => sum + (s[key] as number), 0) / n).toFixed(1);

	return (
		<ModalSectionCard
			borderRadiusSize="sm"
			className={styles['averages-section']}
		>
			<div className={styles['row']}>
				<div className={styles['cover-image-placeholder']} />
				<div className={styles['score']}>
					<div className={styles['average-score']}>{avg('personalScore')}</div>
				</div>
				<div className={styles['score']}>
					<div className={styles['average-score']}>{avg('teamScore')}</div>
				</div>
				<div className={styles['score']}>
					<div className={styles['average-score']}>{avg('congregationalScore')}</div>
				</div>
			</div>
		</ModalSectionCard>
	);
};
